import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import { TOKEN_TIME_BEFORE_EXPIRATION } from "@/constants/util.constants";
import {
  authenticate,
  sendRefreshTokens,
} from "@/models/socket/authenticate.model";
import AppProvider from "@/services/AppContext/appProvider";
import {
  getLSAuthToken,
  getLSRefreshToken,
  isTokenAboutToExpire,
  isTokenExpired,
  setLSAuthToken,
  setLSRefreshToken,
} from "@/utils/tokens.util";
import { jwtDecode } from "jwt-decode";
import React, { useLayoutEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";

const ProtectedRoute: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckComplete, setIsCheckComplete] = useState(false);
  const [isAuthed, setIsAuthed] = useState<"yes" | "no" | "loading">("loading");

  async function checkUserTokens() {
    const authToken = getLSAuthToken();
    const refreshToken = getLSRefreshToken();
    if (authToken && refreshToken) {
      const decodedAuth = jwtDecode(authToken);
      const decodedRefresh = jwtDecode(refreshToken);
      // console.log("Decoded Auth Token: ", decodedAuth);
      // console.log("Decoded Refresh Token: ", decodedRefresh);
    }

    // console.log(authToken);
    if (
      !authToken ||
      authToken === "undefined" ||
      !refreshToken ||
      refreshToken === "undefined"
    ) {
      setIsLoggedIn(false);
      setIsCheckComplete(true);
      return;
    }

    if (isTokenExpired(refreshToken)) {
      setIsLoggedIn(false);
      setIsCheckComplete(true);
      return;
    }

    if (isTokenAboutToExpire(refreshToken)) {
      console.log("Refresh token is about to expire");
      const response = await sendRefreshTokens(refreshToken);
      console.log(response);
      if (response.rStatus === "error") {
        setIsLoggedIn(false);
        setIsCheckComplete(true);
        return;
      }
      setLSAuthToken(response.rData.authToken);
      setLSRefreshToken(response.rData.refreshToken);
    }
    console.log("Auth token is valid");
    setIsLoggedIn(true);
    setIsCheckComplete(true);
  }

  useLayoutEffect(() => {
    // check if user is valid in every half of the token expiration time
    checkUserTokens();
    const interval = setInterval(() => {
      checkUserTokens();
    }, TOKEN_TIME_BEFORE_EXPIRATION / 2);
    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    async function checkAuthentication() {
      const refreshToken = getLSRefreshToken();
      if (!refreshToken || refreshToken === "undefined") {
        setIsAuthed("loading");
        return;
      }
      const response = await authenticate(refreshToken);
      if (response.rStatus === "error") {
        console.error("Authentication failed:", response.rData);
        setIsAuthed("no");
        return;
      }
      setIsAuthed("yes");
    }

    if (!isCheckComplete) {
      return;
    }
    if (!isLoggedIn) {
      return;
    }

    checkAuthentication();
  }, [isLoggedIn, isCheckComplete]);

  return !isCheckComplete ? (
    // If the check is not complete, show a loading spinner
    <div className="container m-auto">
      <div className="flex h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-neutral-900"></div>
      </div>
    </div>
  ) : isLoggedIn ? (
    isAuthed === "yes" ? (
      <AppProvider>
        <Outlet />
      </AppProvider>
    ) : isAuthed === "loading" ? (
      <div>
        <div className="flex h-screen items-center justify-center">
          <div className="h-32 w-32 animate-spin rounded-full border-b-6 border-white"></div>
        </div>
        <div className="flex h-screen items-center justify-center">
          <div className="h-32 w-32 animate-spin rounded-full border-b-6 border-white"></div>
        </div>
        <div className="fixed inset-0 flex items-center justify-center">
          <h2 className="mt-14 text-xl text-white md:mt-0">Logging in...</h2>
        </div>
      </div>
    ) : (
      <Navigate to={ROUTE_LOGIN_OUT} />
    )
  ) : (
    <Navigate to={ROUTE_LOGIN_OUT} />
  );
};

export default ProtectedRoute;
